// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@flip/XDL/Utilities";
import color from "@flip/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@flip/XDL/Utilities/links";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import styled, { css } from "styled-components";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 8px;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 0;

        ${StyledClickable} {
            display: flex;
            width: 100%;

            &:not(:last-of-type) {
                margin-bottom: 4px;
            }

            ${/* sc-selector */ StyledTypography} {
                font-size: 14px;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 22px;
                color: ${resolveColor("text", theme)};
                text-transform: none;
                width: 100%;

                &:hover {
                    color: ${resolveColor("common.ui13", theme)};
                }
            }
        }
    `;
};

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.mobile-full-width,
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};

export const createLinkIconStyles = (icon: React.ComponentType | string, linkStyles?: LinkPresentationProps) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "left" : "right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: 12,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 14,
                        css: css`
                            margin-${marginPosition}: 4px;
                            opacity: 0.5;
                        `,
                    },
                    position: "left",
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            padding-top: 10px;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;
            justify-content: space-between;
            padding-top: 20px;
        `,
    },
    cancelButton: {
        variant: "tertiary",
        color: "primary",
        css: css`
            border-width: 1px;
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-top: 10px;
                margin-right: 0px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const StyledBox = styled(StyledWrapper)`
    width: 100%;
    display: flex;
    padding: 18px;
    border: 1px solid ${color("common.border")};
    background-color: ${color("common.ui1")};
    box-shadow: inset 0 2px 0 0 ${color("secondary")};
    ${avoidBreakInsidePrintElement}
`;

export const sharedBreadcrumbsPageStyles = css`
    &&&& {
        padding-top: 16px;
        padding-bottom: 20px;
    }
`;

export const sharedBadgesContainerStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;
